import { CheckOutlined, CloseSquareTwoTone, MinusSquareFilled } from '@ant-design/icons';
import { Button, Card, Col, Descriptions, Divider, Row, Space, Steps, Typography } from 'antd';
import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Trans, useTranslation } from 'react-i18next';
import { Outlet } from 'react-router-dom';

import useAPIQuery from '../../api/useAPIQuery';
import ApproveRequestOrStageModal from '../../components/ApproveRequestOrStageModal';
import ErrorResult from '../../components/ErrorResult';
import PageContent from '../../components/PageContent';
import RejectRequestJobPostingModal from '../../components/RejectRequestJobPostingModal';
import RequestJobPostingTabs from '../../components/RequestJobPostingTabs/RequestJobPostingTabs';
import usePermissionCheckerQuery from '../../hooks/usePermissionCheckerQuery';
import useTypedParams from '../../hooks/useTypedParams';
import { withRouteChange } from '../../services/APM';
import { RequestJobPostingId, RequestJobPostingStageWithApprover } from '../../types';
import { currencyFormatter, HireModelLabel, WorkModelLabel } from '../../utils';
import styles from './RequestJobPostingAnalysis.module.css';

const RequestJobPostingAnalysis = () => {
  const { t } = useTranslation();

  const { id } = useTypedParams({ id: RequestJobPostingId });
  const [openApproveRequestOrStage, setOpenApproveRequestOrStage] = useState(false);
  const [openRejectRequestJobPosting, setOpenRejectRequestJobPosting] = useState(false);
  const config = useAPIQuery('/configs/tenant-configurations/');

  const user = useAPIQuery('/users/me/');

  const canApproveMarkedRequests = usePermissionCheckerQuery([
    'can_approve_marked_request_job_postings',
  ]);
  const canApproveAllRequests = usePermissionCheckerQuery(['can_approve_all_request_job_postings']);

  const requestJobPosting = useAPIQuery(`/request-job-postings/${id}/`);

  const currentUserStageInProgress = requestJobPosting.data?.stages.find(
    (stage) => stage.status === 'IN_PROGRESS'
  );

  const lastStage =
    (requestJobPosting.data?.stages.length ?? 0) - 1 === currentUserStageInProgress?.sequence;

  const canApproveRequest =
    user.data?.is_staff ||
    (currentUserStageInProgress?.approver.id === user.data?.id && canApproveMarkedRequests.data) ||
    (config.data?.t010_enableNewApproverProfile && canApproveAllRequests.data);

  return (
    <>
      {openApproveRequestOrStage && (
        <ApproveRequestOrStageModal
          requestJobPostingId={id}
          currentStage={currentUserStageInProgress as RequestJobPostingStageWithApprover}
          isLastStage={lastStage}
          open={openApproveRequestOrStage}
          onCancel={() => setOpenApproveRequestOrStage(false)}
          onSuccess={() => {
            setOpenApproveRequestOrStage(false);
            requestJobPosting.refetch();
          }}
          destroyOnClose
        />
      )}
      {openRejectRequestJobPosting && (
        <RejectRequestJobPostingModal
          requestJobPostingId={id}
          currentStage={currentUserStageInProgress as RequestJobPostingStageWithApprover}
          open={openRejectRequestJobPosting}
          onCancel={() => setOpenRejectRequestJobPosting(false)}
          onSuccess={() => {
            setOpenRejectRequestJobPosting(false);
            requestJobPosting.refetch();
          }}
          destroyOnClose
        />
      )}
      <ErrorResult error={requestJobPosting.error}>
        <Helmet>
          <title>
            {t('Analisar requisição')} | {requestJobPosting.data?.name || '...'} |{' '}
            {t('Recrutamento e Seleção')}
          </title>
        </Helmet>
        <PageContent>
          <Outlet />
          <Card data-testid="main-card">
            <Space direction="vertical" size="middle">
              <Col span={24}>
                <RequestJobPostingTabs />
              </Col>
              <Row>
                <Col span={11}>
                  <Typography.Title level={4} style={{ color: '#166889' }}>
                    <Trans>Fluxo</Trans>
                  </Typography.Title>
                  <Steps
                    className={[styles.stepItemIcon, styles.stepItemIconSvg].join(' ')}
                    current={
                      requestJobPosting.data?.status === 'APPROVED'
                        ? requestJobPosting.data.stages.length + 1
                        : requestJobPosting.data?.status === 'REJECTED'
                        ? requestJobPosting.data?.stages.find(
                            (stage) => stage.status === 'REJECTED'
                          )?.sequence || undefined
                        : requestJobPosting.data?.stages.find(
                            (stage) => stage.status === 'IN_PROGRESS'
                          )?.sequence || undefined
                    }
                    direction="vertical"
                    items={requestJobPosting.data?.stages.map((stage) => ({
                      title: stage.name,
                      description: stage.approver.full_name,
                      icon:
                        stage.status === 'REJECTED' ? (
                          <CloseSquareTwoTone
                            twoToneColor="#f51505"
                            style={{ width: '100%', height: '100%' }}
                          />
                        ) : undefined,
                    }))}
                  />
                </Col>
                <Col span={1}>
                  <Divider type="vertical" style={{ height: '100%' }} />
                </Col>
                <Col span={11}>
                  <Descriptions
                    title={
                      <Typography.Title level={4} style={{ color: '#166889' }}>
                        <Trans>Dados</Trans>
                      </Typography.Title>
                    }
                    column={1}
                    labelStyle={{ color: 'var(--ant-primary-color)' }}
                  >
                    <Descriptions.Item label={t('Modelo de requisição')}>
                      {requestJobPosting.data?.template_name}
                    </Descriptions.Item>
                    <Descriptions.Item label={t('Tipo de requisição')}>
                      {requestJobPosting.data?.type === 'STAFF_INCREASE'
                        ? t('Aumento de Quadro')
                        : t('Substituição')}
                    </Descriptions.Item>
                    {requestJobPosting.data?.type === 'SUBSTITUTION' && (
                      <Descriptions.Item label={t('Nome do substituído')}>
                        {requestJobPosting.data.employee_name_being_replaced || '-'}
                      </Descriptions.Item>
                    )}
                    <Descriptions.Item label={t('Nome da requisição')}>
                      {requestJobPosting.data?.name}
                    </Descriptions.Item>
                    <Descriptions.Item label={t('Quantidade de posições')}>
                      {requestJobPosting.data?.positions}
                    </Descriptions.Item>
                    <Descriptions.Item label={t('Modelo de contratação')}>
                      <HireModelLabel value={requestJobPosting.data?.hire_model} />
                    </Descriptions.Item>
                    <Descriptions.Item label={t('Modelo de trabalho')}>
                      <WorkModelLabel value={requestJobPosting.data?.work_model} />
                    </Descriptions.Item>
                    <Descriptions.Item label={t('Faixa salarial')}>
                      {requestJobPosting.data?.start_salary && requestJobPosting.data?.end_salary
                        ? t(`R$ {{start_salary}} a R$ {{end_salary}}`, {
                            start_salary: currencyFormatter(requestJobPosting.data?.start_salary),
                            end_salary: currencyFormatter(requestJobPosting.data?.end_salary),
                          })
                        : requestJobPosting.data?.end_salary &&
                          !!requestJobPosting.data?.start_salary
                        ? `${t('R$')} ${currencyFormatter(requestJobPosting.data.end_salary)}`
                        : t('Sem faixa salarial')}
                    </Descriptions.Item>
                    <Descriptions.Item label={t('Unidade')}>
                      {requestJobPosting.data?.branch || '-'}
                    </Descriptions.Item>
                  </Descriptions>
                  <Row>
                    {currentUserStageInProgress && canApproveRequest ? (
                      <Col span={2} offset={16}>
                        <Space>
                          <Button
                            type="primary"
                            danger
                            icon={<MinusSquareFilled />}
                            disabled={
                              !['NOT_INITIATED', 'IN_PROGRESS'].includes(
                                requestJobPosting.data?.status || ''
                              )
                            }
                            onClick={() => {
                              setOpenRejectRequestJobPosting(true);
                            }}
                          >
                            {' '}
                            <Trans>Reprovar</Trans>
                          </Button>
                          <Button
                            type="primary"
                            icon={<CheckOutlined />}
                            disabled={
                              !['NOT_INITIATED', 'IN_PROGRESS'].includes(
                                requestJobPosting.data?.status || ''
                              )
                            }
                            onClick={() => {
                              setOpenApproveRequestOrStage(true);
                            }}
                          >
                            {' '}
                            <Trans>Aprovar</Trans>
                          </Button>
                        </Space>
                      </Col>
                    ) : null}
                  </Row>
                </Col>
              </Row>
            </Space>
          </Card>
        </PageContent>
      </ErrorResult>
    </>
  );
};

export default withRouteChange()(RequestJobPostingAnalysis);
